import { Suspense, useRef } from 'react'
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router'
import { I18nextProvider } from 'react-i18next'
import { Toaster } from '@/components/Toaster'
import { AppProviders } from './app/providers'
import { ThemeProvider } from './app/providers/ThemeProvider'
import { AppRouter } from './app/router/index.unified'
import { clearChunkRetry, lazyWithRetry } from './app/router/lazyWithRetry'
import i18n from './app/i18n'
import { AuthProvider, Authenticated, AuthLoading, Unauthenticated, isAuthCallbackPath, useAuth } from '@agion/auth'
import type { AuthConfig } from '@agion/auth'
import { getRequiredConfig } from './shared/config/runtime-env'
import { Spinner, Text } from '@agion/ui'
import { isOnboardingOrCallbackPath } from './shared/lib/onboarding-paths'
import { UIVariantProvider } from './shared/ui-variant'
import './styles/globals.css'

// If we got here, the app loaded successfully — clear stale-chunk retry flag
clearChunkRetry()
const SSOCallbackPage = lazyWithRetry(() => import('./pages/SSOCallbackPage').then(m => ({ default: m.SSOCallbackPage })))

// Clear stale zustand cache BEFORE zustand rehydrates
localStorage.removeItem('agion-unified-auth')

const authConfig: AuthConfig = {
  authUrl: getRequiredConfig('VITE_AUTH_URL', 'http://localhost:8080'),
  apiUrl: getRequiredConfig('VITE_API_URL', 'http://localhost:8080'),
  loginUrl: getRequiredConfig('VITE_LOGIN_URL', 'http://localhost:3000'),
  appName: 'admin-ui',
  hasAuthCallbackRoute: true,
  mockAuth:
    (window.__RUNTIME_CONFIG__?.VITE_MOCK_AUTH === 'true') ||
    (import.meta.env['VITE_MOCK_AUTH'] === 'true') ||
    (import.meta.env['VITE_AUTH_DISABLED'] === 'true'),
}

/**
 * OnboardingGuard — admin-ui specific routing based on onboardingState.
 *
 * Reads sessionData from @agion/auth and routes:
 * - pending_approval → /onboarding/pending
 * - no_organization → /onboarding/create-organization
 * - active_member → render children (admin UI)
 *
 * SECURITY: This check is synchronous (not useEffect) to prevent flash of admin UI.
 */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { sessionData, user } = useAuth()
  const navigate = useNavigate()
  const hasNavigatedRef = useRef(false)

  const onboardingState = sessionData?.onboardingState
  const isOnboardingPath = isOnboardingOrCallbackPath(window.location.pathname)

  // Allow onboarding/callback pages to render regardless of state
  if (isOnboardingPath) {
    return <>{children}</>
  }

  if (onboardingState === 'pending_approval') {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true
      // Use setTimeout to avoid navigate during render
      setTimeout(() => {
        navigate('/onboarding/pending', {
          replace: true,
          state: {
            email: user?.email,
            organizationName: sessionData?.organizationName,
          },
        })
      }, 0)
    }
    return null
  }

  if (onboardingState === 'no_organization' || (!user?.organizationId && onboardingState !== 'active_member')) {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true
      setTimeout(() => {
        navigate('/onboarding/create-organization', {
          replace: true,
          state: {
            email: user?.email,
            domain: user?.email?.split('@')[1],
          },
        })
      }, 0)
    }
    return null
  }

  // active_member — render admin UI
  return <>{children}</>
}

/** Redirect to SSO login when unauthenticated */
function SSORedirect() {
  const { client } = useAuth()
  const hasRedirectedRef = useRef(false)

  if (!hasRedirectedRef.current && client) {
    hasRedirectedRef.current = true
    const returnPath = window.location.pathname + window.location.search
    client.initiateSSO(returnPath).catch(console.error)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <Text color="secondary">{i18n.t('common:loading', 'Loading...')}</Text>
      </div>
    </div>
  )
}

function AppContent() {
  const { pathname } = useLocation()
  // A callback establishes authentication; it cannot require it beforehand.
  if (isAuthCallbackPath(pathname)) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>}>
        <SSOCallbackPage />
      </Suspense>
    )
  }

  return (
    <>
      <AuthLoading>
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <Text color="secondary">{i18n.t('common:loading', 'Loading...')}</Text>
          </div>
        </div>
      </AuthLoading>
      <Authenticated>
        <OnboardingGuard>
          <UIVariantProvider>
            <AppProviders>
              <AppRouter />
            </AppProviders>
          </UIVariantProvider>
        </OnboardingGuard>
      </Authenticated>
      <Unauthenticated>
        <SSORedirect />
      </Unauthenticated>
    </>
  )
}

function App() {
  return (
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <AuthProvider config={authConfig}>
          <Router>
            <AppContent />
          </Router>
          <Toaster />
        </AuthProvider>
      </I18nextProvider>
    </ThemeProvider>
  )
}

export default App
